import { useMemo } from "react"
import { createFileRoute } from "@tanstack/react-router"
import { type ColumnDef } from "@tanstack/react-table"
import { ScrollText } from "lucide-react"
import { Badge } from "@/components/ui/badge"
import { EntityTablePage } from "@/features/shared/components/entity-table-page"
import { useChangeLogs } from "@/lib/hooks/use-changelog"
import { useSites } from "@/lib/hooks/use-sites"
import type { ChangeLog, ChangeLogDetail } from "@/types/api"

export const Route = createFileRoute("/_authenticated/change-log")({
  component: ChangeLogPage,
})

interface ChangeLogRow {
  log: ChangeLog
  detail: ChangeLogDetail
}

const changeTypeVariant: Record<string, "default" | "success" | "warning" | "destructive"> = {
  added: "success",
  changed: "warning",
  deleted: "destructive",
}

function buildReference(log: ChangeLog, detail: ChangeLogDetail): string {
  const middle =
    log.action === "creation" || log.action === "deletion"
      ? log.action
      : detail.name
  return [log.model_class, middle, log.object_id].join(" - ")
}

function formatValue(value: unknown): string {
  if (value === null || value === undefined) return "—"
  return String(value)
}

function getColumns(siteNameMap: Map<string, string>): ColumnDef<ChangeLogRow, unknown>[] {
  return [
    {
      id: "name",
      header: "Name",
      cell: ({ row }) => {
        const { user } = row.original.log
        const name = `${user.first_name} ${user.last_name}`.trim()
        return name || "—"
      },
    },
    {
      id: "email",
      header: "Email",
      cell: ({ row }) => row.original.log.user.email || "—",
    },
    {
      id: "site",
      header: "Site",
      cell: ({ row }) =>
        siteNameMap.get(row.original.log.site) ?? row.original.log.site,
    },
    {
      id: "change_type",
      header: "Change Type",
      cell: ({ row }) => (
        <Badge variant={changeTypeVariant[row.original.detail.change_type] ?? "default"}>
          {row.original.detail.change_type}
        </Badge>
      ),
    },
    {
      id: "reference",
      header: "Reference",
      cell: ({ row }) => (
        <span className="max-w-[300px] truncate block">
          {buildReference(row.original.log, row.original.detail)}
        </span>
      ),
    },
    {
      id: "old_value",
      header: "Old Value",
      cell: ({ row }) => (
        <span className="max-w-[200px] truncate block">
          {formatValue(row.original.detail.old)}
        </span>
      ),
    },
    {
      id: "new_value",
      header: "New Value",
      cell: ({ row }) => (
        <span className="max-w-[200px] truncate block">
          {formatValue(row.original.detail.new)}
        </span>
      ),
    },
    {
      id: "updated_at",
      header: "Updated At",
      cell: ({ row }) =>
        new Date(row.original.log.action_time).toLocaleString(undefined, {
          year: "numeric",
          month: "numeric",
          day: "numeric",
          hour: "2-digit",
          minute: "2-digit",
        }),
    },
  ]
}

function ChangeLogPage() {
  const { data: logs, isLoading, isError, error } = useChangeLogs()
  const { data: sites } = useSites()

  const siteNameMap = useMemo(() => {
    const map = new Map<string, string>()
    sites?.forEach((s) => map.set(s.slug, s.name))
    return map
  }, [sites])

  const rows = useMemo<ChangeLogRow[]>(() => {
    if (!logs) return []
    return logs.flatMap((log) =>
      log.change_details.map((detail) => ({ log, detail })),
    )
  }, [logs])

  const columns = useMemo(() => getColumns(siteNameMap), [siteNameMap])

  return (
    <EntityTablePage
      icon={<ScrollText className="h-5 w-5 text-[var(--color-text-secondary)]" />}
      title="Change Log"
      description={`${rows.length} entries`}
      searchPlaceholder="Search logs..."
      emptyMessage="No change log entries"
      columns={columns}
      data={rows}
      isLoading={isLoading}
      isError={isError}
      error={error}
    />
  )
}
